import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoadingSpinner } from '@/components/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/contexts/auth'
import { captureError } from '@/lib/monitoring'

type AuthMode = 'signin' | 'signup' | 'forgot'

interface EmailAuthFormProps {
  onSuccess?: () => void
  onError?: (error: string) => void
}

export function EmailAuthForm({ onSuccess, onError }: EmailAuthFormProps) {
  const { t } = useTranslation()
  const { signInWithEmail, signUpWithEmail, resetPassword } = useAuth()

  const [mode, setMode] = useState<AuthMode>('signin')
  const [isLoading, setIsLoading] = useState(false)
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validatePassword = (password: string) => {
    return password.length >= 6
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.email) {
      newErrors.email = t('auth.emailRequired')
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('auth.emailInvalid')
    }

    if (mode !== 'forgot') {
      if (!formData.password) {
        newErrors.password = t('auth.passwordRequired')
      } else if (!validatePassword(formData.password)) {
        newErrors.password = t('auth.passwordTooShort')
      }

      if (mode === 'signup') {
        if (!formData.fullName.trim()) {
          newErrors.fullName = t('auth.nameRequired')
        }

        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = t('auth.passwordsDoNotMatch')
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)

    try {
      if (mode === 'signin') {
        await signInWithEmail({
          email: formData.email,
          password: formData.password,
        })
        onSuccess?.()
      } else if (mode === 'signup') {
        await signUpWithEmail({
          email: formData.email,
          password: formData.password,
          fullName: formData.fullName,
        })
        onSuccess?.()
      } else if (mode === 'forgot') {
        await resetPassword(formData.email)
        setResetEmailSent(true)
      }
    } catch (error) {
      captureError(error as Error, { context: `emailAuth_${mode}` })
      onError?.(getErrorMessage(error as Error))
    } finally {
      setIsLoading(false)
    }
  }

  const getErrorMessage = (error: Error | { message?: string }) => {
    if (error?.message) {
      // Map common Supabase error messages
      if (error.message.includes('Invalid login credentials')) {
        return t('auth.invalidCredentials')
      }
      if (error.message.includes('Email not confirmed')) {
        return t('auth.emailNotConfirmed')
      }
      if (error.message.includes('User already registered')) {
        return t('auth.userAlreadyExists')
      }
    }
    return t('auth.generalError')
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  const renderTitle = () => {
    switch (mode) {
      case 'signin':
        return t('auth.signInWithEmail')
      case 'signup':
        return t('auth.createAccount')
      case 'forgot':
        return t('auth.resetPassword')
    }
  }

  // Show reset email confirmation
  if (resetEmailSent) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 mx-auto bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
            <svg
              className="w-6 h-6 text-blue-600 dark:text-blue-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">{t('auth.checkYourEmail')}</h2>
            <p className="text-sm text-muted-foreground mt-2">
              {t('auth.resetEmailSent', { email: formData.email })}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t('auth.resetEmailInstructions')}</p>
          </div>
        </div>

        <div className="text-center space-y-2">
          <button
            type="button"
            onClick={() => {
              setResetEmailSent(false)
              setMode('signin')
              setFormData({ email: '', password: '', confirmPassword: '', fullName: '' })
            }}
            className="text-sm text-primary hover:underline transition-colors"
          >
            {t('auth.backToSignIn')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-foreground">{renderTitle()}</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'signup' && (
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-1">
              {t('auth.fullName')}
            </label>
            <Input
              id="fullName"
              type="text"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className={errors.fullName ? 'border-error' : ''}
              disabled={isLoading}
              placeholder={t('auth.fullNamePlaceholder')}
            />
            {errors.fullName && <p className="text-error text-sm mt-1">{errors.fullName}</p>}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
            {t('auth.email')}
          </label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={errors.email ? 'border-error' : ''}
            disabled={isLoading}
            placeholder={t('auth.emailPlaceholder')}
          />
          {errors.email && <p className="text-error text-sm mt-1">{errors.email}</p>}
        </div>

        {mode !== 'forgot' && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
              {t('auth.password')}
            </label>
            <Input
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={errors.password ? 'border-error' : ''}
              disabled={isLoading}
              placeholder={t('auth.passwordPlaceholder')}
            />
            {errors.password && <p className="text-error text-sm mt-1">{errors.password}</p>}
          </div>
        )}

        {mode === 'signup' && (
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-foreground mb-1"
            >
              {t('auth.confirmPassword')}
            </label>
            <Input
              id="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={errors.confirmPassword ? 'border-error' : ''}
              disabled={isLoading}
              placeholder={t('auth.confirmPasswordPlaceholder')}
            />
            {errors.confirmPassword && (
              <p className="text-error text-sm mt-1">{errors.confirmPassword}</p>
            )}
          </div>
        )}

        <Button type="submit" disabled={isLoading} size="lg" className="w-full">
          {isLoading ? <LoadingSpinner size="sm" /> : renderTitle()}
        </Button>
      </form>

      <div className="text-center space-y-2">
        {mode === 'signin' && (
          <>
            <button
              type="button"
              onClick={() => setMode('forgot')}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              disabled={isLoading}
            >
              {t('auth.forgotPassword')}
            </button>
            <div className="text-sm text-muted-foreground">
              {t('auth.noAccount')}{' '}
              <button
                type="button"
                onClick={() => setMode('signup')}
                className="text-primary hover:underline"
                disabled={isLoading}
              >
                {t('auth.signUp')}
              </button>
            </div>
          </>
        )}

        {mode === 'signup' && (
          <div className="text-sm text-muted-foreground">
            {t('auth.haveAccount')}{' '}
            <button
              type="button"
              onClick={() => setMode('signin')}
              className="text-primary hover:underline"
              disabled={isLoading}
            >
              {t('auth.signIn')}
            </button>
          </div>
        )}

        {mode === 'forgot' && (
          <button
            type="button"
            onClick={() => setMode('signin')}
            className="text-sm text-primary hover:underline"
            disabled={isLoading}
          >
            {t('auth.backToSignIn')}
          </button>
        )}
      </div>
    </div>
  )
}
